import { useUnifiedProfilesActions, useUnifiedProfilesValues } from '@/features/unifiedProfiles/context';
import { appendUnifiedSearch } from '@/features/unifiedProfiles/unifiedProfilesSlice';
import { useKeydown } from '@/hooks/useKeydown';
import { Button } from '@/lib';
import { RootState, useAppDispatch } from '@/reducers';
import { useClickOutside, useFocusTrap, useMergedRef } from '@mantine/hooks';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import { UnifiedProfileSearchInput } from './UnifiedProfileSearchInput';
import { UnifiedProfileSearchOptions } from './UnifiedProfileSearchOptions';

export const UnifiedProfileSearchbar: FC = () => {
  const { t } = useTranslation('unifiedProfile');
  const dispatch = useAppDispatch();

  const [isOptionsShown, setIsOptionsShown] = useState(false);
  const setOptionsVisibility = useCallback(
    (value: boolean) => {
      setIsOptionsShown(value);
    },
    [setIsOptionsShown]
  );

  const useClickOutsideRef = useClickOutside(() => setOptionsVisibility(false));
  const focusTrapRef = useFocusTrap();
  const ref = useMergedRef(useClickOutsideRef, focusTrapRef);

  const hasSavedSearches = useSelector((state: RootState) => state.unifiedProfiles.lastSearches.length > 0);

  const { searchText } = useUnifiedProfilesValues();
  const { setSearchText } = useUnifiedProfilesActions();
  const [searchValue, setSearchValue] = useState(searchText);

  const isSearchButtonDisabled = searchValue === searchText || searchValue?.length <= 2;

  const handleSearchClick = useCallback(async () => {
    if (isSearchButtonDisabled) {
      return;
    }

    setSearchText(searchValue);
    dispatch(appendUnifiedSearch(searchValue));
    setOptionsVisibility(false);
  }, [isSearchButtonDisabled, searchValue, setSearchText, appendUnifiedSearch, searchValue, setOptionsVisibility]);

  useKeydown('Escape', () => setOptionsVisibility(false));

  return (
    <div
      data-opened={isOptionsShown && hasSavedSearches}
      className={twMerge('flex w-1/2 min-w-[380px] max-w-[550px]', searchText ? 'items-end gap-2' : 'flex-col gap-6 py-20 px-8')}
    >
      <div className={twMerge('flex flex-col', searchText ? 'gap-2' : 'gap-6')}>
        <p className={twMerge('text-sm font-medium text-secondary-700', !searchText && 'font-semibold')}>{t('searchInUnifiedProfiles')}</p>
        <div ref={ref} className='relative'>
          <UnifiedProfileSearchInput
            value={searchValue}
            className={twMerge(!searchText && 'h-full py-6 text-md')}
            onChange={setSearchValue}
            executeSearch={handleSearchClick}
            setOptionsVisibility={setOptionsVisibility}
          />
          {isOptionsShown ? <UnifiedProfileSearchOptions setSearchInput={setSearchValue} setOptionsVisibility={setOptionsVisibility} /> : null}
        </div>
      </div>

      <Button onClick={handleSearchClick} disabled={isSearchButtonDisabled} intent='primary' className={twMerge(!searchText && 'p-3 w-full')}>
        {t('search')}
      </Button>
    </div>
  );
};
